import shp from 'shpjs'; // Modificato import
import Papa, { ParseResult } from 'papaparse';
import { gpx, kml } from '@tmcw/togeojson';
import { Geometry } from 'wkx'; // Import corretto per wkx
import { FeatureCollection, GeoJSON, Feature, BBox as GeoJSONBBox } from 'geojson'; // Rinominato BBox importato per chiarezza
import bbox from '@turf/bbox'; // Importa bbox da turf
import { CsvProcessingOptions } from '../types'; // Importa il nuovo tipo

// Usa il tipo LayerBase['maplibreLayerType'] dallo store per coerenza
import type { LayerBase, BBox } from '../../../store/types'; // Importa LayerBase e BBox dallo store
type MaplibreLayerType = LayerBase['maplibreLayerType']; // Usa il tipo dallo store

// Tipi Helper
// Aggiorna la firma per usare il tipo MaplibreLayerType corretto e includere BBox opzionale
// Deve corrispondere alla firma di addLayer in layerSlice (considerando i parametri usati)
type AddGeojsonLayerFunc = (
  geojson: GeoJSON, // Corrisponde a 'data' nella firma di addLayer (GeoJSON | undefined, ma qui è sempre definito)
  layerName: string, // Corrisponde a 'name'
  maplibreLayerType: MaplibreLayerType, // Corrisponde a 'maplibreLayerType'
  originalFormat?: string, // Corrisponde a 'originalFormat'
  maplibreSourceId?: string, // Corrisponde a 'maplibreSourceId'
  extent?: BBox, // Corrisponde a 'extent', usa BBox da store/types ([number, number, number, number])
  maplibreLayerId?: string // Corrisponde a 'maplibreLayerId'
) => string; // Corrisponde al tipo di ritorno di addLayer (layerId)

// --- Funzioni di Lettura File ---

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

// --- Funzioni di Parsing e Conversione ---

// Funzione helper per convertire DMS in gradi decimali
const parseDMSToDecimal = (dmsString: string): number | null => {
  if (!dmsString || typeof dmsString !== 'string') {
    return null;
  }

  const dms = String(dmsString).trim().toUpperCase();

  // Regex per estrarre gradi, minuti, secondi e direzione (N, S, E, W)
  // Supporta vari delimitatori comuni (spazi, °, ', ") e la direzione alla fine o come parte dei numeri.
  // Esempi: 45°30'15.5"N, 45 30 15.5 N, 453015.5N, -10 30 15.5 W (W è ridondante se c'è -)
  const regex = /(-?\d+(?:\.\d+)?)[°\sD]*([0-5]?\d(?:[.,]\d+)?)?['\sM\u2032]*([0-5]?\d(?:[.,]\d+)?)?["\sS\u2033]*(N|S|E|W)?/i;
  const match = dms.match(regex);

  if (!match) {
    // Prova a vedere se è già un numero decimale valido (es. per coordinate miste)
    const num = parseFloat(dmsString.replace(',', '.'));
    if (!isNaN(num)) return num;
    return null; // Non è stato possibile parsare
  }

  const degreesStr = match[1];
  const minutesStr = match[2];
  const secondsStr = match[3];
  const direction = match[4] || ''; // N, S, E, W o stringa vuota

  const degrees = parseFloat(degreesStr.replace(',', '.'));
  const minutes = minutesStr ? parseFloat(minutesStr.replace(',', '.')) : 0;
  const seconds = secondsStr ? parseFloat(secondsStr.replace(',', '.')) : 0;

  if (isNaN(degrees) || isNaN(minutes) || isNaN(seconds)) {
    return null; // Valori non validi
  }

  let decimalDegrees = degrees + (minutes / 60) + (seconds / 3600);

  // Applica segno negativo per Sud e Ovest, a meno che i gradi non siano già negativi
  if (degrees >= 0) { // Solo se i gradi non sono già negativi
    if (direction === 'S' || direction === 'W') {
      decimalDegrees *= -1;
    }
  } else { // Se i gradi sono già negativi, la direzione S/W è ridondante o potrebbe essere un errore
    if ((direction === 'N' && decimalDegrees < 0) || (direction === 'E' && decimalDegrees < 0)) {
      // es. -45N -> -45,  -10E -> -10. Questo è corretto.
    } else if ((direction === 'S' && decimalDegrees > 0) || (direction === 'W' && decimalDegrees > 0)) {
      // es. 45S -> -45. Se fosse -45S, diventa -45.
      // Questo caso è già gestito dal fatto che degrees è negativo.
      // Non dovrebbe succedere se degrees è già negativo e la direzione è S/W,
      // a meno che non sia tipo -45S, che è un doppio negativo implicito.
      // La logica attuale lo lascerebbe -45.
    }
  }
  return decimalDegrees;
};


const parseCsv = async (csvFile: File, csvOptions?: CsvProcessingOptions): Promise<FeatureCollection> => {
  const csvText = await readFileAsText(csvFile);
  return new Promise<FeatureCollection>((resolve, reject) => { // Specifica il tipo della Promise
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<Record<string, any>>) => { // Aggiungi tipo a results
        try {
          const data = results.data; // Non serve castare se il tipo è corretto
          const errors = results.errors;

          // Gestisci errori di parsing di PapaParse
          if (errors.length > 0) {
              // Potresti decidere di continuare o rigettare qui
              // reject(new Error(`Errore parsing CSV: ${errors.map(e => e.message).join(', ')}`));
              // return;
          }

          if (!data || data.length === 0) {
             reject(new Error(`CSV ${csvFile.name} vuoto o dati non trovati.`));
             return; // Aggiunto return
          }

          // Usa le opzioni fornite da CsvProcessingOptions
          const geometryType = csvOptions?.geometryType;
          const latField = csvOptions?.latField;
          const lonField = csvOptions?.lonField;
          const wktField = csvOptions?.wktField;
          const isDMS = csvOptions?.isDMS || false; // Recupera l'opzione isDMS
        
          // Validazione robusta delle opzioni e degli header
          if (!geometryType) {
            reject(new Error(`CSV ${csvFile.name}: Tipo di geometria non specificato nelle opzioni.`));
            return;
          }

          const realHeaders = Object.keys(data[0]); // Header reali dal primo record

          if (geometryType === 'latlon') {
            if (!latField || !lonField) {
              reject(new Error(`CSV ${csvFile.name}: Campi Latitudine o Longitudine non specificati per il tipo 'latlon'.`));
              return;
            }
            if (!realHeaders.includes(latField)) {
              reject(new Error(`CSV ${csvFile.name}: La colonna Latitudine selezionata ('${latField}') non esiste nel file.`));
              return;
            }
            if (!realHeaders.includes(lonField)) {
              reject(new Error(`CSV ${csvFile.name}: La colonna Longitudine selezionata ('${lonField}') non esiste nel file.`));
              return;
            }
          } else if (geometryType === 'wkt') {
            if (!wktField) {
              reject(new Error(`CSV ${csvFile.name}: Campo WKT non specificato per il tipo 'wkt'.`));
              return;
            }
            if (!realHeaders.includes(wktField)) {
              reject(new Error(`CSV ${csvFile.name}: La colonna WKT selezionata ('${wktField}') non esiste nel file.`));
              return;
            }
          }

          const features: Feature[] = []; // Usa Feature da geojson

          if (geometryType === 'latlon' && latField && lonField) {
            data.forEach((row: Record<string, any>) => { // Aggiungi tipi
              // Usa i nomi reali dei campi trovati
              const latVal = row[latField];
              const lonVal = row[lonField];
              
              let lat: number | null = null;
              let lon: number | null = null;

              if (isDMS) {
                lat = parseDMSToDecimal(String(latVal));
                lon = parseDMSToDecimal(String(lonVal));
              } else {
                const tempLat = typeof latVal === 'string' ? parseFloat(latVal.replace(',', '.')) : (typeof latVal === 'number' ? latVal : NaN);
                const tempLon = typeof lonVal === 'string' ? parseFloat(lonVal.replace(',', '.')) : (typeof lonVal === 'number' ? lonVal : NaN);
                if (!isNaN(tempLat)) lat = tempLat;
                if (!isNaN(tempLon)) lon = tempLon;
              }

              if (lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon)) {
                const properties = { ...row };
                // Rimuovi i campi originali dalle properties se si desidera
                // delete properties[latField!];
                // delete properties[lonField!];
                features.push({
                  type: 'Feature',
                  geometry: { type: 'Point', coordinates: [lon, lat] },
                  properties: properties,
                });
              } else { // Coordinate non valide
              }
            }); // Fine data.forEach per Lat/Lon
          } else if (geometryType === 'wkt' && wktField) {
             data.forEach((row: Record<string, any>, _index: number) => { // Aggiungi tipi
                const wktString = row[wktField]; // Usa nome reale
                if (typeof wktString === 'string' && wktString.trim()) {
                    try {
                        // Usa la funzione corretta da wkx
                        const geometry = Geometry.parse(wktString).toGeoJSON();
                        const properties = { ...row };
                        // Rimuovi campo WKT originale se si desidera
                        // delete properties[wktField];
                        features.push({
                            type: 'Feature',
                            geometry: geometry as GeoJSON.Geometry, // Aggiunto cast
                            properties: properties,
                        });
                    } catch (parseError) {
                        // console.warn(`Errore parsing WKT per la riga ${_index}: ${wktString}`, parseError);
                    }
                } else {
                    // console.warn(`Valore WKT mancante o non stringa per la riga ${_index}`);
                }
             });
          } else {
            // Se geometryType è null o non è 'latlon' o 'wkt', o se i campi non sono forniti
            // Questo potrebbe essere gestito con un'auto-rilevazione o un errore più specifico
            // Per ora, se le opzioni non sono sufficienti, non si genereranno features.
            // La validazione all'inizio della funzione dovrebbe già aver catturato i casi di opzioni mancanti.
            if (data.length > 0) { // Solo se c'erano dati ma nessuna feature valida a causa delle opzioni
                 reject(new Error(`CSV ${csvFile.name}: Opzioni di geometria non sufficienti o non valide per generare features.`));
                 return;
            }
          }

          if (features.length === 0 && data.length > 0) {
             reject(new Error(`CSV ${csvFile.name}: Nessuna feature valida generata. Controlla i valori nelle colonne coordinate selezionate e le opzioni di importazione.`));
             return;
          }

          // Risolvi con la FeatureCollection creata
          resolve({ type: 'FeatureCollection', features });

        } catch (error) {
          reject(new Error(`Errore elaborazione interna CSV: ${csvFile.name}`));
        }
      },
      error: (error: Error) => { // Aggiungi tipo a error
        reject(new Error(`Errore parsing CSV ${csvFile.name}: ${error.message}`));
      }
    });
  });
};

const parseGpx = async (gpxFile: File): Promise<FeatureCollection> => { // Modificato tipo ritorno
  try {
    const gpxText = await readFileAsText(gpxFile);
    const xmlDoc = new DOMParser().parseFromString(gpxText, 'text/xml');
    // Verifica errori di parsing XML
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
        throw new Error(`Errore parsing XML nel file GPX: ${parserError.textContent || 'Errore sconosciuto'}`);
    }
    const geojson = gpx(xmlDoc);
    // togeojson può restituire null o FeatureCollection vuota
    if (!geojson || !geojson.features || geojson.features.length === 0) {
        throw new Error(`Nessuna feature valida trovata nel file GPX ${gpxFile.name}.`);
    }
    // Filtra feature con geometria nulla
    const validFeatures = geojson.features.filter(f => f.geometry);
    if (validFeatures.length === 0) {
         throw new Error(`Nessuna feature valida (con geometria) trovata nel file GPX ${gpxFile.name}.`);
    }
    return { type: 'FeatureCollection', features: validFeatures as Feature<GeoJSON.Geometry>[] }; // Asserzione tipo
  } catch (error) {
    throw new Error(`Errore parsing GPX: ${gpxFile.name}`);
  }
};

const parseKml = async (kmlFile: File): Promise<FeatureCollection> => { // Modificato tipo ritorno
  try {
    const kmlText = await readFileAsText(kmlFile);
    const xmlDoc = new DOMParser().parseFromString(kmlText, 'text/xml');
     // Verifica errori di parsing XML
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
        throw new Error(`Errore parsing XML nel file KML: ${parserError.textContent || 'Errore sconosciuto'}`);
    }
    const geojson = kml(xmlDoc);
    // togeojson può restituire null o FeatureCollection vuota
     if (!geojson || !geojson.features || geojson.features.length === 0) {
        throw new Error(`Nessuna feature valida trovata nel file KML ${kmlFile.name}.`);
    }
    // Filtra feature con geometria nulla
    const validFeatures = geojson.features.filter(f => f.geometry);
     if (validFeatures.length === 0) {
         throw new Error(`Nessuna feature valida (con geometria) trovata nel file KML ${kmlFile.name}.`);
    }
    return { type: 'FeatureCollection', features: validFeatures as Feature<GeoJSON.Geometry>[] }; // Asserzione tipo
  } catch (error) {
    throw new Error(`Errore parsing KML: ${kmlFile.name}`);
  }
};

const parseGeojson = async (geojsonFile: File): Promise<GeoJSON> => {
  try {
    const geojsonText = await readFileAsText(geojsonFile);
    const geojson = JSON.parse(geojsonText);
    // Validazione di base
    if (!geojson || typeof geojson !== 'object' || !geojson.type) {
       throw new Error('Formato GeoJSON non valido: manca la proprietà "type".');
    }
    // Potrebbe essere Feature, FeatureCollection, Geometry, etc.
    // Per ora accettiamo qualsiasi GeoJSON valido, la validazione più stretta
    // può avvenire nello store o nel componente MapView.
    // if (geojson.type !== 'FeatureCollection') {
    //   console.warn(`GeoJSON ${geojsonFile.name} non è una FeatureCollection (tipo: ${geojson.type}). Potrebbe non essere visualizzato correttamente.`);
    // }
    return geojson as GeoJSON;
  } catch (error) {
    throw new Error(`Errore parsing GeoJSON: ${geojsonFile.name}`);
  }
};


// --- Nuova Funzione Parsing Shapefile da ZIP ---

const parseShapefileZip = async (zipBuffer: ArrayBuffer): Promise<FeatureCollection> => {
  try {
    // Passa l'intero buffer dello zip a shp()
    const geojson: FeatureCollection | FeatureCollection[] = await shp(zipBuffer); // Modificata chiamata

    // Gestisci il caso in cui shpjs restituisca un array
    let resultGeojson: FeatureCollection;
    if (Array.isArray(geojson)) {
        if (geojson.length === 0) {
            throw new Error('Nessuna FeatureCollection trovata nell\'archivio zip.');
        }
        resultGeojson = geojson[0];
    } else {
        resultGeojson = geojson;
    }


    // Verifica se il risultato è valido
    if (!resultGeojson || resultGeojson.type !== 'FeatureCollection' || !resultGeojson.features) {
      throw new Error('La libreria shpjs non ha restituito una FeatureCollection valida dallo zip.');
    }

    // Filtra eventuali feature senza geometria
    const validFeatures = resultGeojson.features.filter(f => f.geometry);
     if (validFeatures.length === 0 && resultGeojson.features.length > 0) {
         throw new Error(`Nessuna feature valida (con geometria) trovata nello Shapefile dentro lo zip.`);
     }

    return { ...resultGeojson, features: validFeatures as Feature<GeoJSON.Geometry>[] };
  } catch (error) {
    // Rendi l'errore più specifico se possibile
    if (error instanceof Error && error.message.includes('but-unzip')) {
         throw new Error(`Errore durante la decompressione dell'archivio zip. Assicurati che sia un file .zip valido contenente uno Shapefile.`);
    }
    throw new Error(`Errore parsing Shapefile da zip: ${error instanceof Error ? error.message : String(error)}`);
  }
};


// --- Funzione Principale di Elaborazione (Modificata per accettare File[]) ---

export const processFiles = async (
  files: File[], // Modificato da FileList a File[]
  maplibreLayerType: MaplibreLayerType,
  addGeojsonLayer: AddGeojsonLayerFunc,
  csvOptions?: CsvProcessingOptions // Aggiorna il tipo del quarto argomento opzionale
): Promise<void> => {

  if (!files || files.length === 0) {
    return;
  }

  // Processiamo solo il primo file dato che l'input non è più multiple
  // TODO: In futuro, potremmo voler iterare su `files` se l'input diventa `multiple`
  const file = files[0];
  if (!file) {
      return;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  const layerName = file.name.replace(/\.[^/.]+$/, "");
  let format: string | undefined = undefined;
  let parsePromise: Promise<GeoJSON> | null = null;

  try {
      switch (extension) {
          case 'zip':
              format = 'shapefile (zip)';
              // Leggi come ArrayBuffer e poi parsa
              const zipBuffer = await readFileAsArrayBuffer(file);
              parsePromise = parseShapefileZip(zipBuffer);
              break;
          case 'csv':
              format = 'csv';
              // Passa le opzioni CSV a parseCsv
              parsePromise = parseCsv(file, csvOptions);
              break;
          case 'gpx':
              format = 'gpx';
              parsePromise = parseGpx(file);
              break;
          case 'kml':
              format = 'kml';
              parsePromise = parseKml(file);
              break;
          case 'geojson':
          case 'json':
              format = 'geojson';
              parsePromise = parseGeojson(file);
              break;
          default:
              throw new Error(`Formato file non supportato: .${extension}`); // Lancia errore per catch
      }

      if (parsePromise) {
          const geojson = await parsePromise;
          let calculatedBbox: GeoJSONBBox | undefined = undefined;
          if (geojson && (geojson.type === 'FeatureCollection' || geojson.type === 'Feature')) {
            // Assicurati che ci siano features prima di calcolare il bbox per FeatureCollection
            if (geojson.type === 'FeatureCollection' && geojson.features.length > 0) {
               calculatedBbox = bbox(geojson);
            } else if (geojson.type === 'Feature' && geojson.geometry) {
               calculatedBbox = bbox(geojson);
            }
          }

          let finalBbox: [number, number, number, number] | undefined = undefined;
          if (calculatedBbox) {
            if (calculatedBbox.length === 6) {
              finalBbox = [calculatedBbox[0], calculatedBbox[1], calculatedBbox[3], calculatedBbox[4]];
            } else if (calculatedBbox.length === 4) {
              finalBbox = calculatedBbox as [number, number, number, number];
            }
          }
          // Chiama addGeojsonLayer (che è addLayer dello store) con la firma corretta:
          // addLayer(data, name, type, originalFormat, maplibreSourceId, extent, maplibreLayerId)
          // Per GeoJSON inline, maplibreSourceId e maplibreLayerId sono spesso derivati o uguali a layerId,
          // gestiti internamente da addLayer. Passiamo undefined per maplibreSourceId.
          addGeojsonLayer(geojson, layerName, maplibreLayerType, format, undefined /* maplibreSourceId */, finalBbox /* extent */);
      }

  } catch (error: unknown) { // Modificato da any a unknown
      // Usa il messaggio dell'errore lanciato dai parser o dallo switch
      // alert(error.message || `Errore nell'importazione del file ${file.name}. Controlla la console per dettagli.`);
      // Rilancia l'errore per essere gestito dal chiamante (in Header.tsx)
      // Se si volesse accedere a error.message, si dovrebbe fare un type check:
      // if (error instanceof Error) { throw error; } else { throw new Error(String(error)); }
      throw error; // Rilancia l'errore originale per una gestione più specifica in Header
  }
};

// Potenziale hook (se necessario stato interno o effetti)
// export const useFileProcessor = () => {
//   const addGeojsonLayer = useGeoData(state => state.addGeojsonLayer);
//
//   const process = async (files: File[], maplibreLayerType: MaplibreLayerType, csvOptions?: CsvCoordinateOptions) => { // Modificato tipo files
//      await processFiles(files, maplibreLayerType, addGeojsonLayer, csvOptions);
//   };
//
//   return { process };
// };